import "@fontsource/barlow";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { QRCodeSVG } from "qrcode.react";
import { ROOM_LOOKUP } from "./roomLookup";
import { useSearchParams, useNavigate } from "react-router-dom";

const RoomStatus = () => {
    const [searchParams] = useSearchParams();
    const slug = searchParams.get("room");
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [now, setNow] = useState(dayjs());

    const roomMeta = slug ? ROOM_LOOKUP[slug.toLowerCase()] : null;
    const roomCalendarId = roomMeta?.resourceId;
    const calendarName = roomMeta?.name;

    useEffect(() => {
        const tick = setInterval(() => {
            setNow(dayjs());
        }, 1000); // update 'now' every second

        return () => clearInterval(tick);
    }, []);

    useEffect(() => {
        if (!roomCalendarId) return;

        const style = document.createElement("style");

        const fetchEvents = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/rooms/status?resourceId=${encodeURIComponent(roomCalendarId)}`);
                const data = await res.json();
                setEvents(data || []);

                style.textContent = `
                    @keyframes pulseRing {
                        0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.4); }
                        70% { box-shadow: 0 0 0 20px rgba(255,255,255,0); }
                        100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
                    }
                    @keyframes alertPulse {
                        0% { background-color: rgba(255, 255, 255, 0.3); }
                        50% { background-color: rgba(255, 255, 255, 0.5); }
                        100% { background-color: rgba(255, 255, 255, 0.3); }
                    }`;
            } catch (err) {
                console.error("Error fetching events:", err);
            }
        };

        document.head.appendChild(style);

        const poller = setInterval(() => {
            fetchEvents();
        }, 10000); // still fetch every 10 seconds

        fetchEvents(); // initial fetch immediately

        return () => {
            clearInterval(poller);
            document.head.removeChild(style);
        };
    }, [roomCalendarId]);

    useEffect(() => {
        const style = document.createElement("style");
        style.textContent = `
            @media (max-width: 768px) {
            .room-layout {
                flex-direction: column !important;
            }
            .sidebar {
                width: 100% !important;
                border-right: none !important;
                border-bottom: 2px solid rgba(0,0,0,0.1);
                max-height: 250px;
                overflow-y: auto;
            }
            .center-display {
                padding-top: 1rem !important;
            }
        }`;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const currentEvent = events.find(event => {
        const start = dayjs(event.start.dateTime);
        const end = dayjs(event.end.dateTime);
        return now.isAfter(start) && now.isBefore(end);
    });

    const nextEvent = events.find(event => {
        const start = dayjs(event.start.dateTime);
        return now.isBefore(start);
    });

    const renderTime = (timeStr) => dayjs(timeStr).format("hh:mm A");

    const getDuration = (start, end) => {
        const s = dayjs(start);
        const e = dayjs(end);
        return e.diff(s, "minute");
    };

    let progressPercent = 0;
    if (currentEvent) {
        const meetingStart = dayjs(currentEvent.start.dateTime);
        const meetingEnd = dayjs(currentEvent.end.dateTime);
        const elapsed = now.diff(meetingStart, 'second');
        const total = meetingEnd.diff(meetingStart, 'second');
        progressPercent = Math.min(100, (elapsed / total) * 100);
    }

    const bookingUrl = `https://calendar.google.com/calendar/u/0/r/eventedit?text=Booking+for+Room+${calendarName}&details=Reserved+via+display&add=${roomCalendarId}`;

    const isOccupied = !!currentEvent;
    const bgColor = isOccupied ? "#b48291" : "#9fc490"; // red or green

    if (!slug || !ROOM_LOOKUP[slug.toLowerCase()]) {
    return (
        <div style={{ padding: "2rem", fontFamily: "Barlow, sans-serif" }}>
        <h2 style={{ fontSize: "2rem", marginBottom: "1.5rem" }}>Select a Room</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
            {Object.keys(ROOM_LOOKUP).slice(0, 5).map((roomKey) => (
            <button
                key={roomKey}
                onClick={() => navigate(`/room?room=${roomKey}`)}
                style={{
                padding: "1rem 2rem",
                fontSize: "1rem",
                backgroundColor: "#19323c",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                }}
            >
                {ROOM_LOOKUP[roomKey].name}
            </button>
            ))}
        </div>
        </div>
    );
    }

    return (
        <>
            <div className="room-layout" style={{...styles.container, background: bgColor }}>
                <div className="sidebar" style={styles.sidebar}>
                    <div style={{ marginBottom: "3rem", lineHeight: "1.2" }}>
                        <div style={{ fontSize: "1.2rem", textTransform: "uppercase", letterSpacing: "1px" }}>
                            {now.format('dddd, MMMM D')}
                        </div>
                        <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                            {now.format('hh:mm A')}
                        </div>
                    </div>

                    {currentEvent && (
                        <>
                            <h4 style={{ fontSize: "1.2rem", marginTop: "1rem", marginBottom: "0.5rem" }}>Current Meeting</h4>
                            {events.filter(event => {
                                const start = dayjs(event.start.dateTime);
                                const end = dayjs(event.end.dateTime);
                                return now.isAfter(start) && now.isBefore(end);
                            }).map((event, index) => {
                                const meetingStart = dayjs(event.start.dateTime);
                                const meetingEnd = dayjs(event.end.dateTime);
                                const elapsed = now.diff(meetingStart, 'second');
                                const total = meetingEnd.diff(meetingStart, 'second');
                                const progressPercent = Math.min(100, (elapsed / total) * 100);

                                return (
                                    <div key={`current-${index}`} style={styles.eventContainer}>
                                        <div style={{ fontWeight: "bold", fontSize: "0.95rem", marginBottom: "0.25rem" }}>
                                            {renderTime(event.start.dateTime)} – {renderTime(event.end.dateTime)}
                                            <span style={{ fontWeight: "normal", fontSize: "0.9rem", marginLeft: "0.5rem" }}>
                                                ({getDuration(event.start.dateTime, event.end.dateTime)} mins)
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}

                    <h4 style={{ fontSize: "1.2rem", marginTop: "1.5rem", marginBottom: "0.5rem" }}>Upcoming Meetings</h4>
                    {events.filter(event => dayjs(event.start.dateTime).isAfter(now)).map((event, index) => {
                        return (
                            <div key={`event-${index}`} style={styles.upcomingEventContainer}>
                                <div style={{ fontWeight: "bold", fontSize: "0.95rem", marginBottom: "0.25rem" }}>
                                    {renderTime(event.start.dateTime)} – {renderTime(event.end.dateTime)}
                                    <span style={{fontWeight: "normal", fontSize: "0.9rem", marginLeft: "0.5rem"}}>
                                        ({getDuration(event.start.dateTime, event.end.dateTime)} mins)
                                    </span>
                                </div>
                                <div style={{ fontSize: "1rem", lineHeight: 1.4 }}>
                                    {event.summary}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="center-display" style={{ flex: 1, padding: "1rem 1rem 2rem", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", position: "relative" }}>

                    <h1 style={styles.calendarName}>
                        {calendarName}
                    </h1>

                    {currentEvent ? (
                        <div style={styles.currentEventContainer}>
    
                            <div style={{...styles.eventCompletionIndicator, background: "conic-gradient(rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.15) " + progressPercent + "%, transparent " + progressPercent + "%)" }}></div>
                            <div style={{ fontSize: "clamp(3rem, 8vw, 6rem)", fontWeight: "bold", marginBottom: "0.5rem", zIndex: 1 }}>
                                In Use
                            </div>
                            <div style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)", opacity: 0.9, zIndex: 1 }}>
                                {(() => {
                                  const remainingSec = dayjs(currentEvent.end.dateTime).diff(now, 'second');
                                  if (remainingSec < 60 && remainingSec > 0) {
                                    return `${remainingSec} ${remainingSec > 1 ? "secs" : "sec"} remaining`;
                                  } else if (remainingSec > 0) {
                                    return `${dayjs(currentEvent.end.dateTime).diff(now, 'minute')} ${dayjs(currentEvent.end.dateTime).diff(now, 'minute') > 1 ? "mins" : "min"} remaining`;
                                  }
                                })()}
                            </div>
                        </div>
                    ) : (
                        <div style={{...styles.availableEventContainer, 
                            backgroundColor: nextEvent && dayjs(nextEvent.start.dateTime).diff(now, 'minute') < 10 
                                ? "rgba(255,255,255,0.3)" 
                                : "rgba(255,255,255,0.0)",
                            boxShadow: nextEvent && dayjs(nextEvent.start.dateTime).diff(now, 'minute') < 10
                                ? "0 0 20px rgba(255,255,255,0.4)"
                                : "none",
                            animation: nextEvent && dayjs(nextEvent.start.dateTime).diff(now, 'minute') < 10
                                ? "alertPulse 1.5s infinite alternate"
                                : "none"
                        }}>
                            <div style={{ fontSize: "clamp(3rem, 8vw, 6rem)", fontWeight: "bold", marginBottom: "0.5rem" }}>
                                Available
                            </div>
                            {nextEvent && (() => {
                                const totalSec = dayjs(nextEvent.start.dateTime).diff(now, 'second');
                                if (totalSec < 60 && totalSec > 0) {
                                    return (
                                        <div style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)", opacity: 0.85 }}>
                                            Next meeting in {totalSec > 1 ? `${totalSec} secs` : `${totalSec} sec`}
                                        </div>
                                    );
                                } else if (totalSec > 0) {
                                    const hrs = Math.floor(totalSec / 3600);
                                    const mins = Math.floor((totalSec % 3600) / 60);
                                    return (
                                        <div style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)", opacity: 0.85 }}>
                                            Next meeting in {hrs > 0 ? `${hrs} hr${hrs > 1 ? 's' : ''} ` : ''}{mins} min
                                        </div>
                                    );
                                }
                            })()}
                        </div>
                    )}
                </div>
            </div >

            <img src="/10xlogo.png" alt="Company Logo" style={styles.logo} />

            {!currentEvent && (
                <>
                    <div style={styles.scanContainer}>
                        <p>Scan to book</p>
                        <QRCodeSVG value={bookingUrl} size={128} />
                    </div>
                </>
            )}
        </>
    );
};

export default RoomStatus;

const styles = {
    container: {
        minHeight: "100vh",
        width: "100vw",
        color: "#19323c",
        fontFamily: "Barlow, sans-serif",
        display: "flex",
        flexDirection: "row"
    },
    sidebar: {
        width: "300px",
        padding: "1rem",
        borderRight: "2px solid rgba(0, 0, 0, 0.1)",
        background: "rgba(255, 255, 255, 0.25)",
        overflowY: "auto"
    },
    eventContainer: {
        display: "flex",
        flexDirection: "column",
        marginBottom: "1.25rem",
        padding: "0.75rem 1rem",
        backgroundColor: "rgba(255,255,255,0.5)",
        borderLeft: "5px solid #19323c",
        borderRadius: "6px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
    },
    upcomingEventContainer: {
        display: "flex",
        flexDirection: "column",
        marginBottom: "1.25rem",
        padding: "0.75rem 1rem",
        backgroundColor:  "rgba(255,255,255,0.1)",
        borderLeft: "4px solid rgba(25, 50, 60, 0.4)",
        borderRadius: "6px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
    },
    calendarName: {
        fontSize: "clamp(2rem, 4rem, 5rem)",
        marginTop: "0.5rem",
        marginBottom: "2rem",
        textAlign: "center",
        width: "100%"
    },
    currentEventContainer: {
        width: "min(70vw, 70vh)",
        height: "min(70vw, 70vh)",
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.4)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        position: "relative",
        boxShadow: "0 0 0 10px rgba(255,255,255,0.3)",
        animation: "pulseRing 2s infinite"
    },
    eventCompletionIndicator: {
        position: "absolute",
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        clipPath: "inset(0 0 0 0)",
        zIndex: 0,
    },
    logo: {
        maxWidth: "120px",
        position: "absolute",
        bottom: "1rem",
        left: "1rem",
        zIndex: 100
    },
    scanContainer: {
        position: "absolute",
        bottom: "1rem",
        right: "1rem",
        background: "#ffffffee",
        padding: "1rem",
        borderRadius: "0.75rem",
        textAlign: "center",
        fontWeight: "bold",
        fontSize: "0.9rem",
        textTransform: "uppercase",
        boxShadow: "0 4px 10px rgba(0,0,0,0.2)"
    },
    availableEventContainer: {
        width: "min(70vw, 70vh)",
        height: "min(70vw, 70vh)",
        borderRadius: "50%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
    }
};
